import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import Highcharts, { ChartSelectionContextObject } from 'highcharts';
import * as DeepMerge from 'deepmerge';

import * as K from 'common/constants';
import { PlotModel, UrlParams } from 'common/types';
import { calcTootipDecimals, elliptifyLabelText, getChartType } from 'common/util/graph';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { searchLegendWidthSelector, windowWidthState } from 'components/common/Managers/LayoutStateManager';
import { getBaseGraphConfig } from 'components/common/Graphs/graphConfigs';
import ScalyrHighcharts from 'components/common/ScalyrHighcharts';
import { calcBarWidth, recalcBarWidths } from 'components/dashboards/DashboardPage/DashboardHighcharts';
import { queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';
import { highchartsStylesState } from 'styles/stylesProvider';

const MAX_LABEL_LEN = 50;

interface PowerQueryChartProps {
  plotData: Record<string, PlotModel[]>;
  graphStyle: string;
  yScale: string;
  lineSmoothing?: string;
  highchartsOptions?: Highcharts.Options;
  chartInstanceCallback?: (chart: Highcharts.Chart) => void;
}

/**
 * Time series chart display for a Power Query result.
 *
 * @prop seriesData             The process list of plots for this chart
 * @prop tooltipLabel           label associated tooltip values
 * @prop highchartsOptions      Any extra Highcharts config to layer over this one
 * @prop chartInstanceCallback  A callback to capture the Highcharts chart instance -- used for resizing
 */
const PowerQueryTimeSeriesChart = ({
  plotData,
  graphStyle,
  yScale,
  lineSmoothing,
  highchartsOptions,
  chartInstanceCallback,
}: PowerQueryChartProps): JSX.Element => {
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const setSearchParams = useSetSearchParams();
  const chartRef = useRef<HTMLDivElement>(null);
  const [chart, setChart] = useState<Highcharts.Chart | null>(null);
  const highchartsStyles = useRecoilValue(highchartsStylesState);
  const windowWidth = useRecoilValue(windowWidthState);
  const searchGraphLegendWidth = useRecoilValue(searchLegendWidthSelector);

  useEffect(() => {
    setTimeout(() => {
      if (chart?.options?.chart) {
        recalcBarWidths(chart);
        chart.reflow();
      }
    }, 0);
  }, [chart, windowWidth, searchGraphLegendWidth]);

  // --------------------------------------------------------------------------------------------------------------- PLOTS
  // Scalyr's preferred date/time format
  const timeFormat = '%a %b %e, %l:%M:%S %p';
  const chartType = getChartType(graphStyle, lineSmoothing);
  const series: Array<Highcharts.SeriesOptionsType> = [];

  let maxValue = Number.MIN_SAFE_INTEGER;
  Object.values(plotData).forEach((plots) => {
    plots.forEach((plot) => {
      const seriesEntry = {
        name: elliptifyLabelText(plot.name || 'Untitled', MAX_LABEL_LEN, true),
        color: plot.color,
        data: plot.data,
        innerSize: '',
        marker: {
          symbol: 'circle',
        },
        pointPadding: 0.05,
        groupPadding: 0,
        borderWidth: 0,
        pointWidth: chartRef?.current ? calcBarWidth(chartRef?.current, plot.data as unknown as Highcharts.Point[]) : 0,
        type: chartType,
        visible: plot.visible,
      };

      series.push(seriesEntry as Highcharts.SeriesOptionsType);
      maxValue = Math.max(maxValue, plot.maxValue || Number.MIN_SAFE_INTEGER);
    });
  });
  const valueDecimals = calcTootipDecimals(maxValue);

  const tooltip = {
    outside: true,
    hideDelay: 0,
    useHTML: true,
    followPointer: true,
    enabled: true,
    crosshairs: true,
    xDateFormat: timeFormat,
    borderColor: 'transparent',
    headerFormat: `
      <table class="DashboardHighcharts__Tooltip">
        <tbody>
      `,
    pointFormat: `
      <tr>
        <td style="color: {series.color}" class="DashboardHighcharts__TooltipPlotName">{series.name} </td>
        <td class="DashboardHighcharts__TooltipValue">{point.y}</td>
      </tr>
      `,
    footerFormat: `
      <tr>
        <td colspan="3" class="DashboardHighcharts__TooltipFooter">
          <div class="DashboardHighcharts__TooltipDateTime">{point.key}</div>
        </td>
      </tr></tbody></table>
      `,
    valueDecimals,
    shared: true,
  };

  // --------------------------------------------------------------------------------------------------------------- CHART OPTIONS
  const dashboardGraphConfig = {
    chart: {
      animation: false,
      type: chartType,
      zoomType: 'x',
      events: {
        // Capture the mouse selection event to update timerange
        selection(e: ChartSelectionContextObject): void {
          e.preventDefault();
          setSearchParams({
            [K.URL_PARAM.START_TIME]: Math.floor(e.xAxis[0].min),
            [K.URL_PARAM.END_TIME]: Math.floor(e.xAxis[0].max),
          } as unknown as UrlParams);
        },
      },
      alignTicks: false,
      style: {
        fontFamily: 'Open Sans',
      },
      marginTop: 10,
      marginBottom: 35,
      spacingTop: 10,
      spacingBottom: 15,
      spacingLeft: 10,
      spacingRight: 10,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      series: {
        pointPadding: 0.05,
        lineWidth: 1.25,
        connectNulls: true,
        states: {
          hover: {
            enabled: true,
            lineWidth: 1.25,
          },
        },
        marker: {
          enabled: false,
        },
      },
    },
    xAxis: { min: parseFloat(startTime), max: parseFloat(endTime) },
    yAxis: {
      type: yScale,
      startOnTick: false,
      endOnTick: false,
      title: {
        text: undefined,
      },
    },
    tooltip,
    series,
  };

  // --------------------------------------------------------------------------------------------------------------- CHART DISPLAY
  // Use the Scalyr defaults, then override for dashboards and possibly theme
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const configOptions = [getBaseGraphConfig(), dashboardGraphConfig, highchartsStyles];
  if (highchartsOptions) {
    configOptions.push(highchartsOptions);
  }
  const hcConfig: Highcharts.Options = DeepMerge.all(configOptions);

  // Let the parent know the Highcharts chart instance
  const afterChartCreated = (newChart: Highcharts.Chart): void => {
    setChart(newChart);
    if (chartInstanceCallback) {
      chartInstanceCallback(newChart);
    }
  };

  // Settings global language options
  Highcharts.setOptions({
    lang: {
      decimalPoint: '.',
      thousandsSep: ',',
    },
  });

  return (
    <div className="PowerQueryResults__Chart" ref={chartRef}>
      <ScalyrHighcharts
        options={hcConfig}
        highcharts={Highcharts}
        containerProps={{ style: { height: '100%', width: '100%' } }}
        callback={afterChartCreated}
      />
    </div>
  );
};

export default PowerQueryTimeSeriesChart;
